import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { PlusCircle, Edit2, Trash2, ChevronLeft, Save, X, Archive, Clock, Bookmark } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Class } from '../../types/supabase';

const AdminClasses = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [newClass, setNewClass] = useState({
    name: '',
    class_number: 1,
    period: '',
    archived: false,
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .order('archived')
        .order('name')
        .order('class_number');

      if (error) {
        throw error;
      }

      setClasses(data);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setError('Erreur lors du chargement des classes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClass = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('classes')
        .insert([{ 
          name: newClass.name, 
          class_number: newClass.class_number,
          period: newClass.period,
          archived: newClass.archived
        }])
        .select();

      if (error) {
        throw error;
      }

      setClasses([...classes, data[0]]);
      setShowModal(false);
      setNewClass({ name: '', class_number: 1, period: '', archived: false });
    } catch (error) {
      console.error('Error adding class:', error);
      setError('Erreur lors de l\'ajout de la classe');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClass = (classObj: Class) => {
    setEditingClass(classObj);
  };

  const handleSaveEdit = async (id: string) => {
    if (!editingClass) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase
        .from('classes')
        .update({ 
          name: editingClass.name, 
          class_number: editingClass.class_number,
          period: editingClass.period,
          archived: editingClass.archived
        })
        .eq('id', id);

      if (error) {
        throw error;
      }

      setClasses(classes.map(c => c.id === id ? editingClass : c));
      setEditingClass(null);
    } catch (error) {
      console.error('Error updating class:', error);
      setError('Erreur lors de la mise à jour de la classe');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingClass(null);
  };

  const handleDeleteClass = async (id: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette classe ? Cette action est irréversible et impactera toutes les matières associées.')) {
      setIsLoading(true);
      setError(null);
      
      try {
        // First, update any subjects that use this class to set class_id to null
        const { error: updateError } = await supabase
          .from('subjects')
          .update({ class_id: null })
          .eq('class_id', id);

        if (updateError) {
          throw updateError;
        }

        // Then delete the class
        const { error } = await supabase
          .from('classes')
          .delete()
          .eq('id', id);

        if (error) {
          throw error;
        }

        setClasses(classes.filter(c => c.id !== id));
      } catch (error) {
        console.error('Error deleting class:', error);
        setError('Erreur lors de la suppression de la classe');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleToggleArchive = async (classObj: Class) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase
        .from('classes')
        .update({ archived: !classObj.archived })
        .eq('id', classObj.id);

      if (error) {
        throw error;
      }

      // Update the local state
      setClasses(classes.map(c => 
        c.id === classObj.id 
          ? { ...c, archived: !c.archived } 
          : c
      ));
    } catch (error) {
      console.error('Error toggling archive status:', error);
      setError('Erreur lors du changement du statut d\'archivage');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <header className="flex justify-between items-center mb-8">
        <div>
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
            <ChevronLeft className="h-4 w-4 mr-1" />
            <span>Retour au tableau de bord</span>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des classes</h1>
          <p className="mt-2 text-gray-600">
            Ajoutez, modifiez et supprimez les classes du système
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary inline-flex items-center"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          Ajouter une classe
        </button>
      </header>

      {error && (
        <div className="bg-error-50 text-error-700 p-4 rounded-md mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nom de la formation</th>
              <th>Numéro de classe</th>
              <th>Période</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="text-center py-8">Chargement...</td>
              </tr>
            ) : classes.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-8">
                  Aucune classe trouvée
                </td>
              </tr>
            ) : (
              classes.map((classObj) => (
                <tr key={classObj.id} className={classObj.archived ? 'bg-gray-50' : ''}>
                  <td>
                    {editingClass?.id === classObj.id ? (
                      <input
                        type="text"
                        value={editingClass.name}
                        onChange={(e) => setEditingClass({ ...editingClass, name: e.target.value })}
                        className="input-field py-1 px-2"
                        required
                      />
                    ) : (
                      <span className={classObj.archived ? 'text-gray-500' : ''}>
                        {classObj.name}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingClass?.id === classObj.id ? (
                      <input
                        type="number"
                        min="1"
                        value={editingClass.class_number}
                        onChange={(e) => setEditingClass({ 
                          ...editingClass, 
                          class_number: parseInt(e.target.value) 
                        })}
                        className="input-field py-1 px-2 w-20"
                        required
                      />
                    ) : (
                      <span className={classObj.archived ? 'text-gray-500' : ''}>
                        {classObj.class_number}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingClass?.id === classObj.id ? (
                      <input
                        type="text"
                        value={editingClass.period}
                        onChange={(e) => setEditingClass({ ...editingClass, period: e.target.value })}
                        className="input-field py-1 px-2"
                        placeholder="ex: 2024-2026"
                        required
                      />
                    ) : (
                      <span className={classObj.archived ? 'text-gray-500' : ''}>
                        {classObj.period}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingClass?.id === classObj.id ? (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`archived-${classObj.id}`}
                          checked={editingClass.archived}
                          onChange={(e) => setEditingClass({ 
                            ...editingClass, 
                            archived: e.target.checked 
                          })}
                          className="mr-2"
                        />
                        <label htmlFor={`archived-${classObj.id}`}>
                          Archivée
                        </label>
                      </div>
                    ) : (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        classObj.archived 
                          ? 'bg-gray-100 text-gray-800' 
                          : 'bg-success-50 text-success-700'
                      }`}>
                        {classObj.archived ? 'Archivée' : 'Active'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingClass?.id === classObj.id ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSaveEdit(classObj.id)}
                          className="p-1 text-success-500 hover:text-success-700 rounded-md"
                          title="Enregistrer"
                        >
                          <Save className="h-5 w-5" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 text-gray-500 hover:text-gray-700 rounded-md"
                          title="Annuler"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditClass(classObj)}
                          className="p-1 text-primary-600 hover:text-primary-800 rounded-md"
                          title="Modifier"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleToggleArchive(classObj)}
                          className="p-1 text-amber-600 hover:text-amber-800 rounded-md"
                          title={classObj.archived ? "Désarchiver" : "Archiver"}
                        >
                          <Archive className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteClass(classObj.id)}
                          className="p-1 text-error-600 hover:text-error-800 rounded-md"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add Class Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md mx-4 animate-slide-up">
            <h3 className="text-xl font-semibold mb-4">Ajouter une classe</h3>
            
            <form onSubmit={handleAddClass}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de la formation <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Bookmark className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="name"
                      value={newClass.name}
                      onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                      className="input-field pl-10"
                      placeholder="ex: BTS NDRC"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="class_number" className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de classe <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="class_number"
                    min="1"
                    value={newClass.class_number}
                    onChange={(e) => setNewClass({ 
                      ...newClass, 
                      class_number: parseInt(e.target.value) || 1 
                    })}
                    className="input-field"
                    placeholder="ex: 1"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="period" className="block text-sm font-medium text-gray-700 mb-1">
                    Période <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="period"
                      value={newClass.period}
                      onChange={(e) => setNewClass({ ...newClass, period: e.target.value })}
                      className="input-field pl-10"
                      placeholder="ex: 2024-2026"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="archived"
                    checked={newClass.archived}
                    onChange={(e) => setNewClass({ 
                      ...newClass, 
                      archived: e.target.checked 
                    })}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="archived" className="ml-2 block text-sm text-gray-700">
                    Archiver cette classe
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end mt-6 space-x-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-outline"
                  disabled={isLoading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Ajout...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClasses;